(() => {
  const STORAGE_KEY = 'wimexia:selectedVehicle';
  const STORAGE_VERSION = 2;
  const BRAND_PARAM = 'wx_vehicle_brand';
  const MODEL_PARAM = 'wx_vehicle_model';
  const VEHICLE_PARAM = 'wx_vehicle';
  const KTYPE_PARAM = 'wx_vehicle_ktype';
  const BEST_SELLER_PARAM = 'wx_best_seller';

  async function requestVehicleCatalog(params = {}) {
    const searchParams = new URLSearchParams(params);
    const endpoint = searchParams.toString()
      ? `/apps/wimexia/tecdoc-vehicles?${searchParams.toString()}`
      : '/apps/wimexia/tecdoc-vehicles';

    const response = await fetch(endpoint, {
      headers: { Accept: 'application/json' },
    });
    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.error || 'Impossible de charger les véhicules.');
    }

    return Array.isArray(data.items) ? data.items : [];
  }

  function replaceOptions(select, items, placeholder, labelCallback) {
    select.replaceChildren();

    const first = document.createElement('option');
    first.value = '';
    first.textContent = placeholder;
    select.appendChild(first);

    for (const item of items) {
      const option = document.createElement('option');
      option.value = item.id;
      option.textContent = labelCallback(item);
      select.appendChild(option);
    }
  }

  function formatYears(variant) {
    if (variant.yearFrom && variant.yearTo) return `${variant.yearFrom}–${variant.yearTo}`;
    if (variant.yearFrom) return `depuis ${variant.yearFrom}`;
    if (variant.yearTo) return `jusqu’à ${variant.yearTo}`;
    return '';
  }

  function formatVariant(variant, prefix) {
    const details = [];
    if (variant.fuel) details.push(variant.fuel);
    if (variant.powerHp) details.push(`${variant.powerHp} ch`);
    if (variant.displacementCc) details.push(`${variant.displacementCc} cm³`);
    const years = formatYears(variant);
    if (years) details.push(years);

    const name = String(
      variant.name ||
      variant.description ||
      variant.vehicleName ||
      variant.typeName ||
      variant.designation ||
      variant.label ||
      '',
    ).replace(/\s+/g, ' ').trim();

    const label = `${prefix} ${name}`.replace(/\s+/g, ' ').trim();
    return details.length ? `${label} — ${details.join(' • ')}` : label;
  }

  function vehicleLabel(variant) {
    return String(
      variant?.name ||
      variant?.description ||
      variant?.vehicleName ||
      variant?.typeName ||
      variant?.designation ||
      variant?.label ||
      '',
    ).replace(/\s+/g, ' ').trim();
  }

  function normalizeBrand(value) {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '');
  }

  function normalizeVehicleValue(value) {
    return String(value || '')
      .trim()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ')
      .toLowerCase();
  }

  function readBrandLogoMap() {
    const script = document.querySelector('[data-wx-brand-logo-map]');
    if (!(script instanceof HTMLScriptElement)) return {};

    try {
      return JSON.parse(script.textContent || '{}');
    } catch {
      return {};
    }
  }

  function currentPersistentParams() {
    const source = new URL(window.location.href);
    return [BRAND_PARAM, MODEL_PARAM, VEHICLE_PARAM, KTYPE_PARAM, BEST_SELLER_PARAM]
      .map((name) => [name, source.searchParams.get(name)])
      .filter(([, value]) => value);
  }

  function preserveVehicleParamsOnLinks(selector) {
    const persistentParams = currentPersistentParams();
    if (!persistentParams.length) return;

    for (const link of document.querySelectorAll(selector)) {
      if (!(link instanceof HTMLAnchorElement)) continue;
      const target = new URL(link.href, window.location.origin);
      for (const [name, value] of persistentParams) target.searchParams.set(name, value);
      link.href = target.pathname + target.search;
    }
  }

  function preserveVehicleParamsOnSubcollections() {
    preserveVehicleParamsOnLinks('[data-wx-subcollection-handle]');
  }

  function preserveVehicleParamsOnCategories() {
    preserveVehicleParamsOnLinks('[data-wx-brand-category-link]');
  }

  function syncVehicleAwareSubcollectionCounts() {
    const params = new URL(window.location.href).searchParams;
    const hasVehicleSelection =
      params.has(BRAND_PARAM) ||
      params.has(MODEL_PARAM) ||
      params.has(VEHICLE_PARAM) ||
      params.has(KTYPE_PARAM);

    for (const count of document.querySelectorAll('[data-wx-subcollection-count]')) {
      if (!(count instanceof HTMLElement)) continue;

      if (hasVehicleSelection) {
        count.textContent = 'Filtré pour votre véhicule';
        count.dataset.wxVehicleAware = 'true';
      } else {
        const nativeLabel = count.dataset.wxNativeCountLabel || '';
        if (nativeLabel) count.textContent = nativeLabel;
        delete count.dataset.wxVehicleAware;
      }
    }
  }

  function buildBrandMark(container, brandLabel, logoUrl) {
    if (!(container instanceof HTMLElement)) return;

    container.replaceChildren();

    if (logoUrl) {
      const image = document.createElement('img');
      image.src = logoUrl;
      image.alt = '';
      image.width = 120;
      image.height = 80;
      container.appendChild(image);
    } else {
      const fallback = document.createElement('span');
      fallback.textContent = brandLabel.slice(0, 1).toUpperCase();
      container.appendChild(fallback);
    }
  }

  function applyBrandVisuals(root, brandLabel) {
    if (!brandLabel) return;

    const logoMap = readBrandLogoMap();
    const normalized = normalizeBrand(brandLabel);
    const logoUrl = logoMap[normalized];
    const title = root.querySelector('[data-wx-collection-hero-title]');
    const isAllCollection = root.dataset.collectionHandle === 'all';
    const activeSupplier = String(root.dataset.activeSupplier || '').trim();
    const bestSellerActive = root.dataset.bestSellerActive === 'true';

    if (isAllCollection && activeSupplier) {
      if (title instanceof HTMLElement) {
        title.textContent = bestSellerActive
          ? `Pièces auto ${activeSupplier} pour ${brandLabel} parmi nos best-sellers`
          : `Pièces auto ${activeSupplier} pour ${brandLabel}`;
      }
      return;
    }

    if (isAllCollection) {
      const brandArt = root.querySelector('[data-wx-brand-only-art]');
      if (brandArt instanceof HTMLElement) brandArt.hidden = false;

      for (const replacement of root.querySelectorAll('[data-wx-brand-replacement]')) {
        if (!(replacement instanceof HTMLElement)) continue;
        buildBrandMark(replacement, brandLabel, logoUrl);
        replacement.hidden = false;
      }

      if (title instanceof HTMLElement) {
        title.textContent = bestSellerActive
          ? `Pièces auto ${brandLabel} parmi nos best-sellers`
          : `Pièces auto ${brandLabel}`;
      }

      return;
    }

    for (const visual of root.querySelectorAll('[data-wx-category-visual]')) {
      if (visual instanceof HTMLElement) visual.hidden = false;
    }

    for (const badge of root.querySelectorAll('[data-wx-brand-badge]')) {
      if (!(badge instanceof HTMLElement)) continue;
      buildBrandMark(badge, brandLabel, logoUrl);
      badge.hidden = false;
    }
  }

  function init(root) {
    if (root.dataset.wxVehicleHeroReady === 'true') return;
    root.dataset.wxVehicleHeroReady = 'true';

    const form = root.querySelector('[data-wx-collection-vehicle-form]');
    const makeSelect = root.querySelector('[data-wx-collection-vehicle-make]');
    const modelSelect = root.querySelector('[data-wx-collection-vehicle-model]');
    const variantSelect = root.querySelector('[data-wx-collection-vehicle-variant]');
    const goButton = root.querySelector('[data-wx-collection-vehicle-go]');
    const error = root.querySelector('[data-wx-collection-vehicle-error]');

    if (!(form instanceof HTMLFormElement) ||
        !(makeSelect instanceof HTMLSelectElement) ||
        !(modelSelect instanceof HTMLSelectElement) ||
        !(variantSelect instanceof HTMLSelectElement) ||
        !(goButton instanceof HTMLButtonElement) ||
        !(error instanceof HTMLElement)) return;

    let variants = [];
    const requestedUrl = new URL(window.location.href);
    const requestedBrand = requestedUrl.searchParams.get(BRAND_PARAM) || '';
    const requestedModel = requestedUrl.searchParams.get(MODEL_PARAM) || '';
    const requestedVehicle = requestedUrl.searchParams.get(VEHICLE_PARAM) || '';
    const requestedKtype = requestedUrl.searchParams.get(KTYPE_PARAM) || '';

    const showError = (message) => {
      error.textContent = message;
      error.hidden = false;
    };

    const clearError = () => {
      error.textContent = '';
      error.hidden = true;
    };

    const loadVariantsForModel = async ({ preselectVehicle = '', preselectKtype = '' } = {}) => {
      clearError();
      variants = [];
      variantSelect.disabled = true;
      goButton.disabled = true;

      if (!modelSelect.value) {
        replaceOptions(variantSelect, [], 'Sélectionnez d’abord un modèle', () => '');
        return;
      }

      const vehiclePrefix = modelSelect.options[modelSelect.selectedIndex]?.textContent?.trim() || '';

      try {
        replaceOptions(variantSelect, [], 'Chargement…', () => '');
        variants = await requestVehicleCatalog({
          makeName: makeSelect.value,
          modelName: modelSelect.value,
        });
        replaceOptions(
          variantSelect,
          variants,
          variants.length ? 'Véhicule' : 'Aucun véhicule disponible',
          (variant) => formatVariant(variant, vehiclePrefix),
        );
        variantSelect.disabled = !variants.length;

        let requestedVariant = null;

        if (preselectKtype) {
          requestedVariant = variants.find(
            (variant) => String(variant?.ktype || '').trim() === String(preselectKtype).trim(),
          );
        }

        if (!requestedVariant && preselectVehicle) {
          const requestedVehicleKey = normalizeVehicleValue(preselectVehicle);
          requestedVariant = variants.find(
            (variant) => normalizeVehicleValue(vehicleLabel(variant)) === requestedVehicleKey,
          );
        }

        if (requestedVariant) {
          variantSelect.value = String(requestedVariant.id);
          goButton.disabled = false;
        }
      } catch (e) {
        console.error(e);
        showError('Impossible de charger les véhicules.');
      }
    };

    const loadModelsForMake = async ({ preselectModel = '', preselectVehicle = '', preselectKtype = '' } = {}) => {
      clearError();
      variants = [];
      modelSelect.disabled = true;
      variantSelect.disabled = true;
      goButton.disabled = true;
      replaceOptions(variantSelect, [], 'Sélectionnez d’abord un modèle', () => '');

      if (!makeSelect.value) {
        replaceOptions(modelSelect, [], 'Sélectionnez d’abord une marque', () => '');
        return;
      }

      const brandLabel = makeSelect.options[makeSelect.selectedIndex]?.textContent?.trim() || '';
      applyBrandVisuals(root, brandLabel);

      try {
        replaceOptions(modelSelect, [], 'Chargement…', () => '');
        const models = await requestVehicleCatalog({ makeName: makeSelect.value });
        replaceOptions(
          modelSelect,
          models,
          models.length ? 'Modèle' : 'Aucun modèle disponible',
          (model) => `${brandLabel} ${model.name}`.trim(),
        );
        modelSelect.disabled = !models.length;

        if (preselectModel && models.length) {
          const requestedModelKey = normalizeVehicleValue(preselectModel);
          const modelOption = Array.from(modelSelect.options).find((candidate) => {
            return (
              normalizeVehicleValue(candidate.value) === requestedModelKey ||
              normalizeVehicleValue(candidate.textContent) === requestedModelKey ||
              normalizeVehicleValue(candidate.textContent).endsWith(' ' + requestedModelKey)
            );
          });

          if (modelOption) {
            modelSelect.value = modelOption.value;
            await loadVariantsForModel({
              preselectVehicle,
              preselectKtype,
            });
          }
        }
      } catch (e) {
        console.error(e);
        showError('Impossible de charger les modèles.');
      }
    };

    const loadMakes = async () => {
      try {
        clearError();
        makeSelect.disabled = true;
        replaceOptions(makeSelect, [], 'Chargement des marques…', () => '');

        const makes = await requestVehicleCatalog();
        replaceOptions(
          makeSelect,
          makes,
          makes.length ? 'Marque' : 'Aucune marque disponible',
          (make) => make.name,
        );
        makeSelect.disabled = !makes.length;

        if (requestedBrand) {
          const requestedKey = normalizeBrand(requestedBrand);
          const option = Array.from(makeSelect.options).find((candidate) => {
            return (
              normalizeBrand(candidate.textContent) === requestedKey ||
              normalizeBrand(candidate.value) === requestedKey
            );
          });

          if (option) {
            makeSelect.value = option.value;
            applyBrandVisuals(root, option.textContent?.trim() || requestedBrand);
            await loadModelsForMake({
              preselectModel: requestedModel,
              preselectVehicle: requestedVehicle,
              preselectKtype: requestedKtype,
            });
          } else {
            applyBrandVisuals(root, requestedBrand);
          }
        }
      } catch (e) {
        console.error(e);
        replaceOptions(makeSelect, [], 'Impossible de charger les marques', () => '');
        makeSelect.disabled = true;
        if (requestedBrand) applyBrandVisuals(root, requestedBrand);
        showError('Impossible de charger le catalogue véhicules.');
      }
    };

    makeSelect.addEventListener('change', loadModelsForMake);

    modelSelect.addEventListener('change', () => {
      void loadVariantsForModel();
    });

    variantSelect.addEventListener('change', () => {
      goButton.disabled = !variantSelect.value;
    });

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      clearError();

      const variant = variants.find((item) => String(item.id) === variantSelect.value);
      if (!variant) {
        showError('Sélectionnez un véhicule.');
        return;
      }

      const brandLabel = variant.makeName ||
        makeSelect.options[makeSelect.selectedIndex]?.textContent?.trim() || '';
      const modelLabel = variant.modelName ||
        modelSelect.options[modelSelect.selectedIndex]?.textContent?.trim() || '';
      const currentCollection = root.dataset.collectionPath || window.location.pathname;
      const target = new URL(currentCollection, window.location.origin);

      const currentUrl = new URL(window.location.href);
      if (currentUrl.searchParams.get(BEST_SELLER_PARAM) === '1') {
        target.searchParams.set(BEST_SELLER_PARAM, '1');
      }

      if (brandLabel) target.searchParams.set(BRAND_PARAM, brandLabel);
      if (modelLabel) target.searchParams.set(MODEL_PARAM, modelLabel);

      const selectedVehicleLabel = vehicleLabel(variant);
      if (selectedVehicleLabel) target.searchParams.set(VEHICLE_PARAM, selectedVehicleLabel);
      if (variant.ktype) target.searchParams.set(KTYPE_PARAM, String(variant.ktype));

      target.searchParams.delete('page');

      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
          storageVersion: STORAGE_VERSION,
          selectionType: 'ktype',
          makeName: brandLabel,
          modelName: modelLabel,
          tecdocVehicleTypeId: variant.id,
          ktype: variant.ktype,
          variant,
          selectedAt: new Date().toISOString(),
        }));
      } catch {}

      window.location.assign(target.pathname + target.search);
    });

    loadMakes();
  }

  const boot = () => {
    document.querySelectorAll('[data-wx-collection-vehicle-hero]').forEach(init);
    preserveVehicleParamsOnSubcollections();
    preserveVehicleParamsOnCategories();
    syncVehicleAwareSubcollectionCounts();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
